import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Thumb, openFile } from "@/components/Customers";
import CameraCapture from "@/components/CameraCapture";

export default function RecordScans({ base, recordId, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const input = useRef(null);

  const load = () => api.get(`${base}/records/${recordId}/scans`).then((r) => setData(r.data)).catch((e) => setErr(formatError(e)));
  useEffect(() => { setData(null); setErr(null); load(); }, [base, recordId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const form = new FormData();
      form.append("file", f, f.name);
      try { await api.post(`${base}/records/${recordId}/scans`, form); ok += 1; } catch (e) { toast.error(`${f.name}: ${formatError(e)}`); }
    }
    if (ok) toast.success(`${ok} scan${ok === 1 ? "" : "s"} added to ${data.customer.name}'s folder`);
    setBusy(false);
    load();
    onChanged?.();
  };

  return (
    <div className="mt-8" data-testid="record-scans">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-xl font-bold">Customer scans {data && <span className="text-muted-foreground text-base font-normal" data-testid="record-scans-count">({data.files.length})</span>}</p>
        {data && <>
          <input ref={input} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="record-scan-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setCamOpen(true)} disabled={busy} data-testid="record-scan-camera-button">
              <Camera size={14} /> Take photo
            </Button>
            <Button size="sm" variant="outline" onClick={() => input.current.click()} disabled={busy} data-testid="record-scan-upload-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Add scan
            </Button>
          </div>
        </>}
      </div>
      {err && <p className="mt-2 text-sm text-muted-foreground" data-testid="record-scans-unavailable">{err}</p>}
      {!data && !err && <Loader2 className="mt-3 animate-spin text-primary" />}
      {data && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="record-scans-grid">
          {data.files.map((f) => (
            <div key={f.id} className="space-y-1" data-testid={`record-scan-${f.id}`}>
              <Thumb file={f} onOpen={openFile} />
              <p className="text-[11px] truncate font-mono">{f.invoice_number || f.original_filename}</p>
            </div>
          ))}
          {!data.files.length && <p className="col-span-full text-sm text-muted-foreground" data-testid="record-scans-empty">No scans for {data.customer.name} yet.</p>}
        </div>
      )}
      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={(file) => upload([file])} />
    </div>
  );
}
